import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiUrl, mockAuthHeaders } from '../lib/api';

export type ImportType = 'sales-orders' | 'inventory' | 'purchase-orders';
export type ImportStatus = 'validating' | 'processing' | 'complete' | 'failed';

export interface ImportRun {
  id: string;
  importType: string;
  status: ImportStatus;
  fileName: string | null;
  rowCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface ImportError {
  row: number;
  field: string;
  message: string;
}

export interface ImportResult {
  success: boolean;
  importRunId?: string;
  created?: number;
  updated?: number;
  total?: number;
  errorCount?: number;
  errors?: ImportError[];
}

async function fetchImportHistory(): Promise<ImportRun[]> {
  const response = await fetch(apiUrl('/api/v1/imports'), {
    headers: {
      'Content-Type': 'application/json',
      ...mockAuthHeaders(),
    },
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('Failed to fetch import history');
  }

  const json = (await response.json()) as { data?: Array<Record<string, unknown>> };
  return (json.data ?? []).map((item) => ({
    id: item.id as string,
    importType: item.importType as string,
    status: item.status as ImportStatus,
    fileName: (item.fileName as string) ?? null,
    rowCount: (item.totalRows as number) ?? (item.processedRows as number) ?? (item.rowCount as number) ?? 0,
    createdAt: (item.startedAt as string) ?? (item.createdAt as string) ?? '',
    updatedAt: (item.finishedAt as string) ?? (item.updatedAt as string) ?? '',
  }));
}

async function uploadImport(type: ImportType, file: File): Promise<ImportResult> {
  const content = await file.text();

  const response = await fetch(apiUrl(`/api/v1/imports/${type}`), {
    method: 'POST',
    headers: {
      'Content-Type': 'text/csv',
      ...mockAuthHeaders(),
    },
    credentials: 'include',
    body: content,
  });

  const data = await response.json();

  if (!response.ok) {
    // Throw the error data so we can display validation errors
    throw data;
  }

  return data;
}

export function useImportHistory() {
  return useQuery({
    queryKey: ['imports'],
    queryFn: fetchImportHistory,
    staleTime: 30000,
  });
}

export function useUploadImport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ type, file }: { type: ImportType; file: File }) =>
      uploadImport(type, file),
    onSuccess: () => {
      // Invalidate import history and any data that may have changed
      queryClient.invalidateQueries({ queryKey: ['imports'] });
      queryClient.invalidateQueries({ queryKey: ['orders'] });
    },
  });
}

async function uploadJsonImport(type: ImportType, rows: unknown[]): Promise<ImportResult> {
  const response = await fetch(apiUrl(`/api/v1/imports/${type}/json`), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...mockAuthHeaders(),
    },
    credentials: 'include',
    body: JSON.stringify({ rows }),
  });
  const json = await response.json();
  if (!response.ok) throw json;
  // API wraps response as { data: { success, created, updated, ... } }
  return (json as { data: ImportResult }).data ?? json;
}

export function useUploadJsonImport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ type, rows }: { type: ImportType; rows: unknown[] }) =>
      uploadJsonImport(type, rows),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['imports'] });
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      queryClient.invalidateQueries({ queryKey: ['procurement'] });
    },
  });
}

export interface WorkbookImportInput {
  inventory?: { rows: unknown[] };
  purchaseOrders?: { rows: unknown[] };
  salesOrders?: { rows: unknown[] };
}

export type WorkbookImportEntry = ImportResult | { error: string };

export interface WorkbookImportResult {
  inventory?: WorkbookImportEntry;
  purchaseOrders?: WorkbookImportEntry;
  salesOrders?: WorkbookImportEntry;
}

async function uploadWorkbook(input: WorkbookImportInput): Promise<WorkbookImportResult> {
  const result: WorkbookImportResult = {};

  // Build list of imports to run in parallel
  type ImportJob = { key: keyof WorkbookImportResult; type: ImportType; rows: unknown[] };
  const jobs: ImportJob[] = [];

  if (input.inventory && input.inventory.rows.length > 0) {
    jobs.push({ key: 'inventory', type: 'inventory', rows: input.inventory.rows });
  }
  if (input.purchaseOrders && input.purchaseOrders.rows.length > 0) {
    jobs.push({ key: 'purchaseOrders', type: 'purchase-orders', rows: input.purchaseOrders.rows });
  }
  if (input.salesOrders && input.salesOrders.rows.length > 0) {
    jobs.push({ key: 'salesOrders', type: 'sales-orders', rows: input.salesOrders.rows });
  }

  const settled = await Promise.allSettled(
    jobs.map((job) => uploadJsonImport(job.type, job.rows))
  );

  for (let i = 0; i < jobs.length; i++) {
    const outcome = settled[i]!;
    if (outcome.status === 'fulfilled') {
      result[jobs[i]!.key] = outcome.value;
    } else {
      const err = outcome.reason;
      result[jobs[i]!.key] = {
        error: err instanceof Error ? err.message : (err?.message ?? err?.error ?? 'Import failed'),
      };
    }
  }

  return result;
}

export function useUploadWorkbook() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: WorkbookImportInput) => uploadWorkbook(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['imports'] });
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      queryClient.invalidateQueries({ queryKey: ['procurement'] });
    },
  });
}
